
// // Localização: frontend/src/hooks/useEquipamentos.js (versão 0.2.3)

import { useState, useEffect, useCallback } from 'react';
// 🛑 Importação correta:
import { 
    get, post, put, remove, // Usar as funções genéricas para CRUD principal
    vincularEquipamentoApi,
    desvincularEquipamentoApi // Importação da função de DESVINCULO
} from '../api/api'; 
import { useAuth } from '../context/AuthContext';

const API_EQUIPAMENTOS = '/equipamentos';
// API_VINCULOS não é mais estritamente necessário se usarmos as funções nomeadas
// const API_VINCULOS = '/equipamentos/vincular'; 

/**
 * Hook customizado para gerenciar a lógica de equipamentos.
 * Inclui CRUD para Equipamentos e funções para Vincular/Desvincular a pacientes.
 */
export const useEquipamentos = () => {
    const { user } = useAuth();
    const [equipamentos, setEquipamentos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Roles que podem gerenciar (Criar, Editar, Excluir, Vincular)
    const canManage = ['admin', 'proprietario', 'gerente'].includes(user?.role?.toLowerCase());

    // ---------------------------------------------------
    // FUNÇÕES DE BUSCA
    // ---------------------------------------------------

    const fetchEquipamentos = useCallback(async () => {
        if (!user) return;

        setLoading(true);
        setError(null);
        try {
            // Usando a função genérica 'get'
            const response = await get(API_EQUIPAMENTOS);
            setEquipamentos(response.data);
        } catch (err) {
            console.error('Erro ao buscar equipamentos:', err);
            setError('Falha ao carregar a lista de equipamentos.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Executa a busca na montagem do componente
    useEffect(() => {
        fetchEquipamentos();
    }, [fetchEquipamentos]);

    // ---------------------------------------------------
    // FUNÇÕES CRUD
    // ---------------------------------------------------

    const saveEquipamento = async (formData, isEditing, equipamentoId) => {
        if (!canManage) return { success: false, message: 'Permissão negada.' };
        
        setLoading(true);
        setError(null);
        try {
            if (isEditing) {
                // Atualização (PUT) - Usando a função genérica 'put'
                await put(`${API_EQUIPAMENTOS}/${equipamentoId}`, formData);
                await fetchEquipamentos();
                return { success: true, message: 'Equipamento atualizado com sucesso!' };
            } else {
                // Criação (POST) - Usando a função genérica 'post'
                const response = await post(API_EQUIPAMENTOS, formData);
                await fetchEquipamentos();
                // Retornar o ID do equipamento criado para permitir criar conta a pagar
                const equipamentoId = response.data?.id || response.data?.data?.id;
                return { 
                    success: true, 
                    message: 'Equipamento criado com sucesso!',
                    equipamentoId: equipamentoId
                };
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Erro de rede ou servidor ao salvar equipamento.';
            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    const deleteEquipamento = async (id) => {
        if (!canManage) return { success: false, message: 'Permissão negada.' };

        setLoading(true);
        setError(null);
        try {
            // Exclusão (DELETE) - Usando a função genérica 'remove'
            await remove(`${API_EQUIPAMENTOS}/${id}`);
            await fetchEquipamentos();
            return { success: true, message: 'Equipamento deletado com sucesso!' };
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Erro ao deletar equipamento.';
            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // ---------------------------------------------------
    // FUNÇÕES DE VÍNCULO
    // ---------------------------------------------------

    const vincularEquipamento = async (paciente_id, equipamento_id) => {
        if (!canManage) return { success: false, message: 'Permissão negada.' };

        setLoading(true);
        setError(null);
        try {
            // Usa a função nomeada do api.js
            const response = await vincularEquipamentoApi({ paciente_id, equipamento_id });
            await fetchEquipamentos(); 
            return { success: true, message: response.data.message };
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Erro ao vincular equipamento.';
            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // A API de desvínculo usa o ID do VÍNCULO (PacienteEquipamentos.id)
    const desvincularEquipamento = async (vinculo_id) => {
        if (!canManage) return { success: false, message: 'Permissão negada.' };

        setLoading(true);
        setError(null);
        try {
            // 🛑 CORREÇÃO CRÍTICA: Usa a função nomeada da API que faz a chamada DELETE 
            const response = await desvincularEquipamentoApi(vinculo_id);
            await fetchEquipamentos(); 
            return { success: true, message: response.data.message };
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Erro ao desvincular equipamento.';
            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    };


    return {
        equipamentos,
        loading,
        error,
        canManage,
        fetchEquipamentos,
        saveEquipamento,
        deleteEquipamento,
        vincularEquipamento,
        desvincularEquipamento,
        disponiveis: equipamentos.filter(e => e.status === 'Disponível'),
        emUso: equipamentos.filter(e => e.status === 'Em Uso'),
    };
};